import { DetailViewModel } from "@frui.ts/datascreens/dist";
import { bound } from "@frui.ts/helpers/dist";
import { Router, watchBusy } from "@frui.ts/screens/dist";
import { attachAutomaticValidator, validateAll } from "@frui.ts/validation/dist";
import type { interfaces } from "inversify";
import cloneDeep from "lodash/cloneDeep";
import { computed, observable, reaction, toJS } from "mobx";
import OptionsService from "services/optionsService";
import { Modules, cancelNavigationName } from "config";
import type LicensePlate from "entities/licensePlate";
import ExemptionRepository from "repositories/exemptionRepository";
import EnumService from "services/enumService";
import EventBus, { exemptionCancel } from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService, { NotificationType } from "services/notificationService";
import formatCountryEnumName from "utils/formatCountryEnumName";
import CountrySelectViewModel from "viewModels/countrySelectViewModel";
import * as Sentry from "@sentry/react";
import type ExemptionChangePreviewResponse from "entities/exemptionChangePreviewResponse";
import ExemptionResponse from "entities/exemptionResponse";
import ExemptionCancellation from "entities/exemptionCancellationRequest";
import { CancelExemptionValidationRules, ExemptionCancellationRequest } from "entities/exemptionCancellationRequest";
import { isSameDay } from "date-fns";
import { czechTodayDate, endOfCzechDay } from "utils/dateUtils";
import { emptyValue } from "utils/emptyValue";

@Router.registerRoute({ route: cancelNavigationName, name: Router.Self })
export default class TerminateExemptionViewModel extends DetailViewModel<ExemptionCancellation> {
  private disposers: Array<() => void> = [];
  private editExemptionPreview: ExemptionChangePreviewResponse;
  countrySelectViewModel: CountrySelectViewModel;
  @observable terminateStep = 1;
  @observable loading = false;
  @observable validUntilDate: Date | undefined;

  constructor(
    private exemption: ExemptionResponse,
    private repository: ExemptionRepository,
    private localizationService: LocalizationService,
    private eventBus: EventBus,
    private notificationService: NotificationService,
    private optionsService: OptionsService,
    private enumService: EnumService,
    private countrySelectViewModelFactory: ReturnType<typeof CountrySelectViewModel.Factory>
  ) {
    super();
    this.navigationName = cancelNavigationName;
    this.countrySelectViewModel = this.countrySelectViewModelFactory("id");
  }

  onInitialize(): Promise<void> {
    this.disposers.push(reaction(() => this.item?.isImmediateCancellation, this.onImmediateCancellationChange));
    this.disposers.push(reaction(() => this.item?.validUntilDate, this.onValidUntilChange));
    return super.onInitialize();
  }

  protected onDeactivate(isClosing: boolean): Promise<any> | void {
    if (isClosing) {
      this.disposers.forEach(dispose => dispose());
      this.disposers = [];
    }

    return super.onDeactivate(isClosing);
  }

  protected loadDetail() {
    const item = new ExemptionCancellation(this.exemption);

    attachAutomaticValidator(item, CancelExemptionValidationRules);

    return item;
  }

  @computed
  get officialRecordId() {
    return this.exemption.officialRecordId ?? "";
  }

  @computed
  get exemptionResponse() {
    return this.exemption;
  }

  @computed
  get isImmediateCancellation() {
    return this.item.isImmediateCancellation ?? false;
  }

  @computed
  get countries() {
    return this.enumService.values("countries").map(country => ({ id: country.id, label: formatCountryEnumName(country) }));
  }

  @computed
  get isDisabled() {
    return this.isImmediateCancellation;
  }

  @computed
  get exemptionReasonOptions() {
    return this.optionsService.enumOptions("exemption_reasons");
  }

  @bound
  getExemptionReasonId(exemptionReason: string) {
    const exemptionReasons = this.enumService.values("exemption_reasons");
    return exemptionReasons.find(x => x.exemptionReason === exemptionReason)?.id ?? emptyValue;
  }

  @bound
  getExemptionReason(id: string) {
    const exemptionReasons = this.enumService.values("notifiable_exemption_reasons");
    return exemptionReasons.find(x => x.id === id)?.exemptionReason;
  }

  @bound
  getExemptionReasonDescription(id: string) {
    const exemptionReasons = this.enumService.values("notifiable_exemption_reasons");
    return exemptionReasons.find(x => x.id === id)?.description;
  }

  @bound
  onImmediateCancellationChange() {
    if (this.item.isImmediateCancellation) {
      this.item.validUntilDate = new Date();
    } else {
      this.item.validUntilDate = endOfCzechDay(this.validUntilDate);
    }
  }

  @bound
  onValidUntilChange() {
    if (!this.item.isImmediateCancellation) {
      this.validUntilDate = endOfCzechDay(this.item.validUntilDate);
      this.item.validUntilDate = endOfCzechDay(this.item.validUntilDate);
    }
  }

  @bound
  @watchBusy
  onBack() {
    this.terminateStep = 1;
  }

  @bound
  @watchBusy
  async onSave() {
    const requestObj = cloneDeep(toJS(this.item));

    if (this.isImmediateCancellation && this.item.validUntilDate) {
      requestObj.validUntilDate = undefined;
    }

    if (!this.isImmediateCancellation && requestObj.validUntilDate && isSameDay(requestObj.validUntilDate, czechTodayDate())) {
      this.item.validUntilDate = endOfCzechDay(requestObj.validUntilDate);
    }
    if (validateAll([this.item]) && this.terminateStep === 2) {
      await this.cancel(this.exemption.exemptionId, requestObj);

      return;
    }

    if (validateAll([this.item]) && this.terminateStep === 1) {
      this.terminateStep = 2;
    }
  }

  @bound
  async cancel(exemptionId: string, exemption: ExemptionCancellationRequest) {
    this.loading = true;

    try {
      await this.repository.cancellation(exemptionId, exemption);
      this.eventBus.publish(exemptionCancel({ entity: exemption }));
      this.notificationService.add({ title: this.translate("cancelled"), type: NotificationType.SUCCESS });
    } catch (e) {
      Sentry.captureException(e);
    }

    this.terminateStep = 1;
    this.loading = false;
    this.requestClose();
  }

  @bound
  onClose() {
    this.requestClose();
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.callCenter, `terminateExemption.${code}`, size);
  }

  translateGeneral(code: string, size?: number) {
    return this.localizationService.translateGeneral(code, size);
  }

  @bound
  translateCountryId(countryId: string) {
    if (!countryId) {
      return emptyValue;
    }
    const country = this.enumService.value("countries", countryId);

    if (!country) {
      return emptyValue;
    }
    return `${country.code} - ${country.name}`;
  }

  translateAttribute(model: "exemption", attribute: keyof ExemptionCancellationRequest, size?: number): string;
  translateAttribute(model: "licensePlate", attribute: keyof LicensePlate, size?: number): string;
  translateAttribute(
    model: "exemption" | "licensePlate",
    attribute: keyof ExemptionCancellationRequest | keyof LicensePlate,
    size?: number
  ) {
    return this.localizationService.translateAttribute(model, attribute, size);
  }

  static Factory({ container }: interfaces.Context) {
    return (exemption: ExemptionResponse) =>
      new TerminateExemptionViewModel(
        exemption,
        container.get(ExemptionRepository),
        container.get(LocalizationService),
        container.get(EventBus),
        container.get(NotificationService),
        container.get(OptionsService),
        container.get(EnumService),
        container.get(CountrySelectViewModel.Factory)
      );
  }
}
